import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, UserX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CheckoutPage() {
  const { items, getTotal } = useCart();
  const { isAuthenticated, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [guestEmail, setGuestEmail] = useState('');
  const [shippingInfo, setShippingInfo] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'USA'
  });

  const subtotal = getTotal();
  const shipping = items.reduce((sum, item) => sum + (item.shipping_rate ?? 15.0), 0);
  const total = subtotal + shipping;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isAuthenticated) {
        // Authenticated checkout
        const orderResponse = await axios.post(
          `${API}/orders`,
          { items, shipping_address: shippingInfo },
          getAuthHeaders()
        );
        const order = orderResponse.data;

        const checkoutResponse = await axios.post(
          `${API}/checkout/session`,
          { order_id: order.id, origin_url: window.location.origin },
          getAuthHeaders()
        );
        window.location.href = checkoutResponse.data.url;
      } else {
        // Guest checkout
        if (!guestEmail) {
          toast.error('Please enter your email address');
          setLoading(false);
          return;
        }

        const orderResponse = await axios.post(`${API}/orders/guest`, {
          items,
          shipping_address: shippingInfo,
          guest_email: guestEmail
        });
        const order = orderResponse.data;

        const checkoutResponse = await axios.post(`${API}/checkout/guest-session`, {
          order_id: order.id,
          origin_url: window.location.origin,
          guest_email: guestEmail
        });
        window.location.href = checkoutResponse.data.url;
      }
    } catch (error) {
      console.error('Checkout failed:', error);
      toast.error(error.response?.data?.detail || 'Checkout failed');
      setLoading(false);
    }
  };

  if (items.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="checkout-page">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <Button
          variant="ghost"
          onClick={() => navigate('/cart')}
          className="mb-8 hover:translate-x-[-4px] transition-transform"
          data-testid="checkout-back-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          BACK TO CART
        </Button>

        <h1 className="font-serif text-4xl md:text-5xl tracking-tight mb-12" data-testid="checkout-title">
          Checkout
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-8">

              {/* Checkout mode indicator */}
              {isAuthenticated ? (
                <div className="flex items-center gap-3 p-4 border border-border rounded-lg bg-muted/30" data-testid="checkout-authenticated">
                  <User className="h-5 w-5 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Checking out with your account</span>
                </div>
              ) : (
                <div className="space-y-4" data-testid="checkout-guest">
                  <div className="flex items-center gap-3 p-4 border border-border rounded-lg bg-muted/30">
                    <UserX className="h-5 w-5 text-muted-foreground" />
                    <div className="flex-1">
                      <span className="text-sm text-muted-foreground">Guest checkout</span>
                      <span className="text-sm text-muted-foreground ml-2">-</span>
                      <button
                        type="button"
                        onClick={() => navigate('/login')}
                        className="text-sm underline ml-2 hover:opacity-70 transition-opacity"
                        data-testid="checkout-login-link"
                      >
                        or sign in
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="email">EMAIL ADDRESS</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="your@email.com"
                      value={guestEmail}
                      onChange={(e) => setGuestEmail(e.target.value)}
                      required
                      data-testid="guest-email-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">We'll send your order confirmation here</p>
                  </div>
                </div>
              )}

              <div>
                <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">
                  SHIPPING INFORMATION
                </h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name">FULL NAME</Label>
                    <Input
                      id="name"
                      value={shippingInfo.name}
                      onChange={(e) => setShippingInfo({ ...shippingInfo, name: e.target.value })}
                      required
                      data-testid="shipping-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="address">ADDRESS</Label>
                    <Input
                      id="address"
                      value={shippingInfo.address}
                      onChange={(e) => setShippingInfo({ ...shippingInfo, address: e.target.value })}
                      required
                      data-testid="shipping-address-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city">CITY</Label>
                      <Input
                        id="city"
                        value={shippingInfo.city}
                        onChange={(e) => setShippingInfo({ ...shippingInfo, city: e.target.value })}
                        required
                        data-testid="shipping-city-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">STATE</Label>
                      <Input
                        id="state"
                        value={shippingInfo.state}
                        onChange={(e) => setShippingInfo({ ...shippingInfo, state: e.target.value })}
                        required
                        data-testid="shipping-state-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="zip">ZIP CODE</Label>
                      <Input
                        id="zip"
                        value={shippingInfo.zip_code}
                        onChange={(e) => setShippingInfo({ ...shippingInfo, zip_code: e.target.value })}
                        required
                        data-testid="shipping-zip-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="country">COUNTRY</Label>
                      <Input
                        id="country"
                        value={shippingInfo.country}
                        onChange={(e) => setShippingInfo({ ...shippingInfo, country: e.target.value })}
                        required
                        data-testid="shipping-country-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                size="lg"
                disabled={loading}
                className="w-full rounded-full px-8 py-6 text-sm uppercase tracking-widest"
                data-testid="proceed-payment-btn"
              >
                {loading ? 'PROCESSING...' : 'PROCEED TO PAYMENT'}
              </Button>
            </form>
          </div>

          <div className="lg:col-span-1">
            <div className="border border-border rounded-lg p-8 sticky top-32">
              <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
                ORDER SUMMARY
              </h2>
              
              <div className="space-y-4 mb-6">
                {items.map((item) => (
                  <div key={item.artwork_id} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{item.artwork_title}</span>
                    <span>${item.price.toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="space-y-4 border-t border-border pt-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span data-testid="checkout-subtotal">${subtotal.toFixed(2)}</span>
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-muted-foreground">Shipping</span>
                    <span data-testid="checkout-shipping">{shipping === 0 ? 'Free' : `$${shipping.toFixed(2)}`}</span>
                  </div>
                  {items.length > 1 && (
                    <div className="text-xs text-muted-foreground space-y-1">
                      {items.map((item) => (
                        <div key={item.artwork_id} className="flex justify-between pl-4">
                          <span>- {item.artwork_title}</span>
                          <span>{(item.shipping_rate ?? 15.0) === 0 ? 'Free' : `$${(item.shipping_rate ?? 15.0).toFixed(2)}`}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tax</span>
                  <span className="text-xs text-muted-foreground italic" data-testid="checkout-tax">Calculated at payment</span>
                </div>
                <div className="border-t border-border pt-4">
                  <div className="flex justify-between text-lg font-medium">
                    <span>Total</span>
                    <span data-testid="checkout-total">${total.toFixed(2)}<span className="text-xs font-normal text-muted-foreground ml-1">+ tax</span></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
